import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useLocalePageMeta } from '../hooks/ui/useLocalePageMeta.js'
import * as TOKENS from './chat/design-tokens'

const TERMS_SECTION_COUNT = 10
const TERMS_PRIVACY_LINK_SECTION_INDEX = 6

const linkClassName = `underline ${TOKENS.BRAND.underline} ${TOKENS.BRAND.accentHover} transition-colors`

/* ── Component ──────────────────────────────────────────────────────── */

const TermsOfService: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')

  useLocalePageMeta('marketing', 'legal.terms', 'https://www.farzadbayat.com/terms', { noindex: true })

  const heading = `text-lg font-bold tracking-tight ${TOKENS.TEXT.primary}`
  const body = `text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`
  const label = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

  return (
    <div className="relative z-50 min-h-[100dvh] w-full overflow-y-auto bg-transparent px-6 py-24 text-foreground custom-scrollbar">
      <div className="mx-auto max-w-3xl">
        <button
          onClick={() => void navigate('/landing', { replace: true })}
          className={`mb-12 inline-flex items-center gap-2 text-sm font-medium ${TOKENS.TEXT.muted} transition-colors ${TOKENS.BRAND.accentHover}`}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t('legal.terms.backLabel')}
        </button>

        <span className={label}>{t('legal.terms.label')}</span>
        <h1 className={`mt-3 text-3xl font-bold tracking-tight md:text-4xl ${TOKENS.TEXT.primary}`}>
          {t('legal.terms.heading')}
        </h1>
        <p className={`mt-4 ${body}`}>{t('legal.terms.lastUpdated')}</p>

        <div className="mt-12 flex flex-col gap-10">
          {Array.from({ length: TERMS_SECTION_COUNT }, (_, index) => (
            <section key={index} className="flex flex-col gap-3">
              <h2 className={heading}>{t(`legal.terms.sections.${index}.title`)}</h2>
              {index === TERMS_PRIVACY_LINK_SECTION_INDEX ? (
                <p className={body}>
                  <Trans
                    i18nKey={`legal.terms.sections.${index}.body`}
                    ns="marketing"
                    components={{
                      1: <a href="/privacy" className={linkClassName} />,
                    }}
                  />
                </p>
              ) : (
                <p className={body}>{t(`legal.terms.sections.${index}.body`)}</p>
              )}
            </section>
          ))}
        </div>

        <div className={`mt-16 border-t pt-8 ${TOKENS.BORDER.default}`}>
          <p className={`text-xs ${TOKENS.TEXT.muted}`}>
            {t('legal.terms.seeAlso')}{' '}
            <a href="/privacy" className={linkClassName}>
              {t('legal.terms.crossLinkLabel')}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
